import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import type { ApiQueryParams } from "@/types/api-types";

export const useCommunityFeedQueries = () => {
  const projectId = useProjectId();
  const api = useApi();

  return {
    messagesInfinite: (query: ApiQueryParams<"getCommunityFeedV2">) => {
      const limit = 20;
      const normalizedQuery = normalizeFeedQuery(query);

      return infiniteQueryOptions({
        queryKey: QUERY_KEYS.MESSAGES_FEED(projectId, normalizedQuery),
        queryFn: ({ pageParam = 0 }) =>
          api
            .getCommunityFeedV2({
              Limit: limit,
              ...normalizedQuery,
              Offset: pageParam * limit,
            })
            .then((items) => commonAPIDataSelector(items)),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => (lastPage?.hasMore ? pages?.length : undefined),
      });
    },
    listPosts: (query: ApiQueryParams<"getCommunityFeedV2">) => {
      const normalizedQuery = normalizeFeedQuery(query);

      return queryOptions({
        queryKey: QUERY_KEYS.MESSAGES_LIST(projectId, normalizedQuery),
        queryFn: () => api.getCommunityFeedV2(normalizedQuery).then((items) => commonAPIDataSelector(items)),
      });
    },
    audience: ({ query }: { query: ApiQueryParams<"getCommunityFeedAudienceV2"> }) =>
      queryOptions({
        queryKey: QUERY_KEYS.MESSAGES_AUDIENCE(projectId, query),
        queryFn: () => api.getCommunityFeedAudienceV2(query),
        select: commonAPIDataSelector,
      }),
    mentionSuggestions: (query: ApiQueryParams<"getMessagesMentionsV1">) =>
      queryOptions({
        queryKey: QUERY_KEYS.MESSAGE_MENTION_SUGGESTIONS(projectId, query),
        queryFn: () => api.getMessagesMentionsV1(query),
        select: commonAPIDataSelector,
        staleTime: 30_000,
      }),
    messageDetails: (messageId: string) =>
      queryOptions({
        queryKey: QUERY_KEYS.MESSAGE_DETAILS(projectId, messageId),
        queryFn: () => api.getMessagesDetailsV2(messageId).then((res) => res.data),
        staleTime: Infinity,
      }),
    commentDetails: ({ messageId, commentId }: { messageId: string; commentId: string }) =>
      queryOptions({
        queryKey: QUERY_KEYS.MESSAGE_COMMENT_DETAILS(projectId, messageId, commentId),
        queryFn: () => api.getMessagesCommentsDetailsV1(messageId, commentId),
        select: commonAPIDataSelector,
        staleTime: Infinity,
      }),
    reactionsInfinite: (messageId: string) => {
      const limit = 5;

      return infiniteQueryOptions({
        queryKey: QUERY_KEYS.MESSAGE_REACTIONS(projectId, messageId),
        queryFn: async ({ pageParam = 0 }) => {
          const { data } = await api.getMessagesReactV1(messageId, {
            Offset: pageParam * limit,
            Limit: limit,
          });

          return data;
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => (lastPage?.hasMore ? pages?.length : undefined),
      });
    },
    commentReactionsInfinite: ({ messageId, commentId }: { messageId: string; commentId: string }) => {
      const limit = 10;

      return infiniteQueryOptions({
        queryKey: QUERY_KEYS.MESSAGE_COMMENT_LIKES(projectId, messageId, commentId),
        queryFn: async ({ pageParam = 0 }) => {
          const { data } = await api.getMessagesCommentsReactV1(messageId, commentId, {
            Offset: pageParam * limit,
            Limit: limit,
          });

          return data;
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => (lastPage?.hasMore ? pages?.length : undefined),
      });
    },
    commentsInfinite: (messageId: string) => {
      const limit = 5;

      return infiniteQueryOptions({
        queryKey: QUERY_KEYS.MESSAGE_COMMENTS(projectId, messageId),
        queryFn: async ({ pageParam = 0 }) => {
          const { data } = await api.getMessagesCommentsV1(messageId, {
            Offset: pageParam * limit,
            Limit: limit,
          });

          return data;
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => (lastPage?.hasMore ? pages?.length : undefined),
        staleTime: 15 * 60 * 1000, // 15 minutes
        refetchOnWindowFocus: true,
      });
    },
    messageTranslation: ({ messageId, languageId }: { messageId: string; languageId: string }) =>
      queryOptions({
        queryKey: QUERY_KEYS.MESSAGE_TRANSLATION(projectId, messageId, languageId),
        queryFn: () => api.getMessagesTranslationsDetailsV2(messageId, languageId),
        select: commonAPIDataSelector,
        retry: false,
      }),
    messageReach: ({ messageId }: { messageId: string }) =>
      queryOptions({
        queryKey: QUERY_KEYS.MESSAGE_REACH(projectId, messageId),
        queryFn: () => api.getMessagesReachV1(messageId),
        select: commonAPIDataSelector,
        retry: false,
      }),
    commentTranslation: ({
      messageId,
      commentId,
      languageId,
    }: {
      messageId: string;
      commentId: string;
      languageId: string;
    }) =>
      queryOptions({
        queryKey: QUERY_KEYS.MESSAGE_COMMENT_TRANSLATION(projectId, messageId, commentId, languageId),
        queryFn: () => api.getMessagesCommentsTranslationsDetailsV2(messageId, commentId, languageId),
        select: commonAPIDataSelector,
        retry: false,
      }),
    listMassMessageAudienceGroups: (query: ApiQueryParams<"getMassMessagesGroupsV1">) =>
      queryOptions({
        queryKey: QUERY_KEYS.MASS_MESSAGES_GROUPS(query),
        queryFn: () => api.getMassMessagesGroupsV1(query),
        select: commonAPIDataSelector,
      }),
    listMassMessageAudienceProjects: (query: ApiQueryParams<"getMassMessagesProjectsV1">) =>
      queryOptions({
        queryKey: QUERY_KEYS.MASS_MESSAGES_PROJECTS(query),
        queryFn: () => api.getMassMessagesProjectsV1(query),
        select: commonAPIDataSelector,
      }),
    listMessageTemplates: (query?: ApiQueryParams<"getMessageTemplatesV1">) =>
      queryOptions({
        queryKey: QUERY_KEYS.MESSAGE_TEMPLATES(query),
        queryFn: () => api.getMessageTemplatesV1(query),
        select: commonAPIDataSelector,
      }),
    adminsToInform: (messageId: string) =>
      queryOptions({
        queryKey: QUERY_KEYS.MESSAGE_ADMINS_TO_INFORM(projectId, messageId),
        queryFn: () => api.getMessagesInformsUsersV1(messageId),
        select: commonAPIDataSelector,
      }),
    projectSummary: (params: { from: string; to: string }) =>
      queryOptions({
        queryKey: QUERY_KEYS.PROJECT_SUMMARY(projectId, params),
        queryFn: () => api.getProjectSummaryV1(params),
        select: commonAPIDataSelector,
        staleTime: 3 * 60 * 1000, // 3 minutes
      }),
    messagesUnreadStats: () =>
      queryOptions({
        queryKey: QUERY_KEYS.MESSAGES_FEED_STATS(projectId),
        queryFn: () => api.getMessagesUnreadStatsV1(),
        select: commonAPIDataSelector,
      }),
    informedAdminsInfinite: (messageId: string) => {
      const limit = 15;

      return infiniteQueryOptions({
        queryKey: QUERY_KEYS.MESSAGE_INFORMED_ADMINS(projectId, messageId),
        queryFn: async ({ pageParam = 0 }) => {
          const { data } = await api.getMessagesInformsV1(messageId, {
            Offset: pageParam * limit,
            Limit: limit,
          });

          return data;
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => (lastPage?.hasMore ? pages?.length : undefined),
      });
    },
  };
};

function normalizeFeedQuery(query: ApiQueryParams<"getCommunityFeedV2">): ApiQueryParams<"getCommunityFeedV2"> {
  const search = query.Query?.trim();

  return { ...query, Query: search && search.length >= 2 ? search : undefined };
}
